import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

import "../home/home.css";
import whyUs from "../../assets/images/why us.jpg";

const testVideoUrl =
  "https://res.cloudinary.com/dtis9hzhi/video/upload/v1760640231/WhatsApp_Video_2025-10-14_at_2.41.01_PM_lhzmp7.mp4";

const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL_PROD
    : process.env.REACT_APP_API_URL_DEV;

// Cache en mémoire pour les données
const dataCache = {
  company: null,
  services: null,
  timestamp: null,
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const Home = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [services, setServices] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState({ company: false, services: false });
  const videoRef = useRef(null);
  const hasAttemptedAutoPlay = useRef(false);

  // Détection mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          userAgent
        );

      setIsMobile(isMobileDevice);
      console.log("Device détecté:", isMobileDevice ? "MOBILE" : "DESKTOP");
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    // AOS init
    setTimeout(() => {
      AOS.init({
        duration: 800,
        once: true,
        easing: "ease-in-out",
      });
    }, 100);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Fetch optimisé avec cache et timeouts
  const fetchWithCache = useCallback(async (url, key) => {
    if (dataCache[key] && Date.now() - dataCache.timestamp < CACHE_DURATION) {
      return dataCache[key];
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          "Cache-Control": "max-age=300",
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      dataCache[key] = data;
      dataCache.timestamp = Date.now();

      return data;
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(`Erreur fetch ${key}:`, error);
      }
      return null;
    }
  }, []);

  // Chargement parallèle des données
  useEffect(() => {
    const loadAllData = async () => {
      setLoading({ company: true, services: true });

      const [companyData, servicesData] = await Promise.allSettled([
        fetchWithCache(`${API_BASE_URL}/company-config/latest`, "company"),
        fetchWithCache(`${API_BASE_URL}/services`, "services"),
      ]);

      if (companyData.status === "fulfilled" && companyData.value) {
        setCompany(companyData.value);
      } else {
        setCompany({
          phone: "(514) 987-6543",
          email: "contact@samyhaul.com",
        });
      }

      if (servicesData.status === "fulfilled" && servicesData.value) {
        if (
          servicesData.value.statusCode === 200 &&
          servicesData.value.data?.services
        ) {
          setServices(servicesData.value.data.services);
        }
      } else {
        setServices([
          {
            id: 1,
            name: "Déménagement Résidentiel",
            description: "Service complet de déménagement pour particuliers",
            imageUrl: "/default-service.jpg",
          },
          {
            id: 2,
            name: "Déménagement Commercial",
            description: "Solutions pour entreprises et commerces",
            imageUrl: "/default-service.jpg",
          },
        ]);
      }

      setLoading({ company: false, services: false });
    };

    loadAllData();
  }, [fetchWithCache]);

  // Fonction pour lire la vidéo
  const handlePlayVideo = useCallback(async () => {
    if (!videoRef.current) return;

    try {
      const video = videoRef.current;

      // S'assurer que la vidéo est prête pour la lecture
      if (video.readyState < 3) {
        await new Promise((resolve) => {
          video.addEventListener("loadeddata", resolve, { once: true });
        });
      }

      video.muted = true;
      await video.play();
      setShowOverlay(false);
    } catch (error) {
      console.error("Erreur lecture vidéo:", error);
      setShowOverlay(true);
    }
  }, []);

  // Autoplay pour MOBILE et DESKTOP
  const handleAutoPlay = useCallback(async () => {
    if (!videoRef.current || !videoLoaded || hasAttemptedAutoPlay.current)
      return;

    try {
      const video = videoRef.current;

      // Vérifier que la vidéo est prête
      if (video.readyState < 3) {
        return;
      }

      // IMPORTANT: Sur mobile, il faut être muted pour l'autoplay
      video.muted = true;
      video.playsInline = true;

      await video.play();
      setShowOverlay(false);
      hasAttemptedAutoPlay.current = true;
      console.log("Autoplay réussi sur:", isMobile ? "MOBILE" : "DESKTOP");
    } catch (err) {
      console.log("Autoplay échoué, affichage overlay");
      setShowOverlay(true);
    }
  }, [isMobile, videoLoaded]);

  // Gestion de l'autoplay - POUR MOBILE ET DESKTOP
  useEffect(() => {
    if (videoLoaded && !hasAttemptedAutoPlay.current) {
      const timer = setTimeout(() => {
        handleAutoPlay();
      }, 500); // Réduit à 500ms pour plus de réactivité

      return () => clearTimeout(timer);
    }
  }, [videoLoaded, handleAutoPlay]);

  const handleNavClick = useCallback((href) => {
    if (href.startsWith("#")) {
      const element = document.querySelector(href);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  // Composants Skeleton
  const PhoneSkeleton = () => (
    <div className="animate-pulse flex items-center">
      <div className="bg-gray-300 rounded-full w-5 h-5 mr-2"></div>
      <div className="h-4 bg-gray-300 rounded w-32"></div>
    </div>
  );

  const ServiceSkeleton = () => (
    <div className="service-card bg-white rounded-2xl shadow-lg overflow-hidden flex-1 animate-pulse">
      <div className="relative h-80 bg-gray-200 overflow-hidden"></div>
      <div className="p-6 text-center">
        <div className="h-6 bg-gray-300 rounded mb-4 mx-auto w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded mb-2 w-5/6 mx-auto"></div>
        <div className="h-4 bg-gray-200 rounded mb-6 w-4/6 mx-auto"></div>
        <div className="h-10 bg-gray-300 rounded-md w-40 mx-auto"></div>
      </div>
    </div>
  );

  return (
    <>
      {/* Section Hero */}
      <section
        id="accueil"
        className="relative hero-gradient text-white pt-24 pb-16 lg:pt-32 lg:pb-24 overflow-hidden w-full"
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900 to-blue-300"></div>
        </div>

     <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
  <div className="flex flex-col-reverse lg:flex-row items-center lg:items-start justify-between gap-8 lg:gap-12 xl:gap-16">
    {/* Texte à gauche */}
    <div
      className="w-full lg:w-1/2 xl:w-[48%] mt-10 lg:mt-0 text-center lg:text-left"
      data-aos="fade-right"
    >
      <div className="mb-8 relative">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap');
          .title-3d {
            font-family: 'Bebas Neue', 'Arial Black', sans-serif;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            line-height: 0.9;
            color: white;
            text-shadow: 3px 3px 0px rgba(0, 0, 0, 0.3), 6px 6px 0px rgba(0, 0, 0, 0.2);
          }
          .title-haul {
            color: #d22819ff;
            display: block;
            margin-top: 0.1em;
            filter: drop-shadow(0 4px 8px rgba(210, 40, 25, 0.4));
          }
        `}</style>

        <h1 className="title-3d text-[3rem] sm:text-[3.8rem] md:text-[clamp(3rem,8vw,6rem)] lg:text-[clamp(3.5rem,7vw,6rem)]">
          Samyy's Haul
        </h1>

        <p className="title-3d">
          <span className="title-haul flex items-center text-[1.6rem] sm:text-[2rem] md:text-[clamp(2rem,6vw,4rem)] lg:text-[clamp(2.5rem,5vw,5rem)]">
            Déménagement
          </span>
        </p>
      </div>

      <div className="mb-8 space-y-3">
        {[
          "Déménagement résidentiel et commercial",
          "Livraison des électroménagers et meubles",
          "Construction et livraison spécialisée",
          "Ramassage de débris",
        ].map((service, index) => (
          <div
            key={index}
            className="flex items-center text-lg lg:text-xl"
          >
            <div className="w-3 h-3 rounded-full mr-4 bg-red-600"></div>
            {service}
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-start items-stretch mt-8">
        <a
          href={`tel:${
            company?.phone?.replace(/[^0-9]/g, "") || "5149876543"
          }`}
          className="group relative inline-flex items-center justify-center bg-white text-blue-800 font-bold py-4 px-6 rounded-lg shadow-xl border-2 border-blue-200 min-h-[65px] w-full sm:w-auto transition-all duration-300 hover:scale-105 hover:bg-blue-50"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
          <span className="text-sm font-semibold whitespace-nowrap">
            {loading.company ? (
              <PhoneSkeleton />
            ) : (
              company?.phone || "(514) 987-6543"
            )}
          </span>
        </a>

        <Link
          to="/déménagement"

          className="group relative inline-flex items-center justify-center bg-blue-900 text-white font-bold py-4 px-6 rounded-lg shadow-xl min-h-[65px] w-full sm:w-auto whitespace-nowrap transition-all duration-300 hover:scale-105 hover:bg-blue-700"
        >
          Demande soumission
        </Link>

        <Link
          to="/collaboration-entreprise"
          className="group relative inline-flex flex-col items-center justify-center bg-red-600 text-white font-bold py-4 px-6 rounded-lg shadow-xl min-h-[65px] w-full sm:w-auto transition-all duration-300 hover:scale-105 hover:bg-red-700 text-center"
        >
          <span className="whitespace-nowrap">
            Demande Collaboration
          </span>
          <span className="whitespace-nowrap text-sm font-normal mt-1">
            Pour Les Entreprises
          </span>
        </Link>
      </div>
    </div>

    {/* Section Vidéo - VERSION CORRIGÉE POUR L'ESPACEMENT */}
   <div className="w-full lg:w-1/2 xl:w-[48%] flex justify-center items-center lg:items-stretch pt-6 lg:pt-0 lg:pl-8 xl:pl-12">
        {/* <div className="relative w-full max-h-[600px] sm:max-h-[850px] lg:max-h-[900px] rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl flex">
        <video
          ref={videoRef}
          src={testVideoUrl}
          muted={true}
          loop
          playsInline
          autoPlay={true}
          preload="auto"
          webkit-playsinline="true"
          className="w-full h-full object-cover lg:object-contain"
          onLoadedData={() => {
            console.log("Vidéo chargée");
            setVideoLoaded(true);
          }}
          onCanPlayThrough={() => {
            console.log("Vidéo prête à jouer");
            setVideoLoaded(true);
          }}
          onPlay={() => {
            console.log("Vidéo en lecture - autoplay réussi");
            setShowOverlay(false);
          }}
          onError={(e) => {
            console.error("Erreur vidéo:", e);
            setShowOverlay(true);
          }}
          onClick={handlePlayVideo}
          onTouchEnd={handlePlayVideo}
        />

        {/* Overlay seulement si l'autoplay échoue */}
      {/*  {showOverlay && (
          <div
            className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/30 flex flex-col items-center justify-center cursor-pointer group"
            onClick={handlePlayVideo}
            onTouchEnd={handlePlayVideo}
          >
            <div className="bg-white/20 backdrop-blur-lg rounded-full p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6 lg:mb-8 group-active:bg-white/30 transition-all duration-200 border border-white/20 shadow-xl">
              <svg
                className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 text-white group-active:scale-95 transition-transform"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>

            <button
              className="bg-blue-600 active:bg-blue-700 text-white px-6 sm:px-10 lg:px-12 py-2 sm:py-3 lg:py-4 rounded-lg lg:rounded-xl font-bold text-sm sm:text-base lg:text-lg transition-all duration-200 active:scale-95 shadow-lg min-w-[140px] sm:min-w-[180px] lg:min-w-[220px] border border-blue-400/30"
              onClick={handlePlayVideo}
            >
              ▶ Lancer la vidéo
            </button>

            <div className="mt-3 sm:mt-4 text-white/70 text-xs sm:text-sm text-center px-4">
              La vidéo devrait démarrer automatiquement
            </div>
          </div>
        )}
      </div>*/}
    </div> 
  </div>
</div>

        <div className="wave-shape">
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
              opacity=".25"
              className="fill-current text-white"
            ></path>
            <path
              d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
              opacity=".5"
              className="fill-current text-white"
            ></path>
            <path
              d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
              className="fill-current text-white"
            ></path>
          </svg>
        </div>
      </section>

      {/* Le reste de votre code reste inchangé... */}
      {/* Contact fixe responsive */}
      <div
        className="fixed z-[9999] flex flex-col space-y-3 bottom-4 left-0 
            p-2 md:p-3 
            bg-white/90 backdrop-blur-2xl shadow-2xl rounded-2xl border-l-4 border-blue-600
            text-sm md:text-base"
      >
        {/* Téléphone */}
        <a
          href={`tel:${company?.phone?.replace(/[^0-9]/g, "") || "5149876543"}`}
          className="flex items-center space-x-2 md:space-x-3 group"
        >
          <div className="bg-blue-100 p-1.5 md:p-2 rounded-full transition-all duration-300 group-hover:bg-blue-600 group-hover:scale-110">
            <svg
              className="w-4 h-4 md:w-5 md:h-5 text-blue-600 group-hover:text-white transition-colors"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
          </div>
          <span className="font-semibold text-gray-700 group-hover:text-blue-600 whitespace-nowrap">
            {loading.company ? (
              <PhoneSkeleton />
            ) : (
              company?.phone || "(514) 987-6543"
            )}
          </span>
        </a>

        {/* Email */}
        <a
          href={`mailto:${company?.email || "contact@samyhaul.com"}`}
          className="flex items-center space-x-2 md:space-x-3 group"
        >
          <div className="bg-blue-100 p-1.5 md:p-2 rounded-full transition-all duration-300 group-hover:bg-blue-600 group-hover:scale-110">
            <svg
              className="w-4 h-4 md:w-5 md:h-5 text-blue-600 group-hover:text-white transition-colors"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <span className="font-semibold text-gray-700 group-hover:text-blue-600 whitespace-nowrap">
            {loading.company ? (
              <div className="h-3 bg-gray-300 rounded w-32 animate-pulse"></div>
            ) : (
              company?.email || "contact@samyhaul.com"
            )}
          </span>
        </a>
      </div>

      {/* Bouton WhatsApp */}
      <a
        href={`https://wa.me/${
          company?.phone?.replace(/[^0-9]/g, "") || "15149876543"
        }`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed right-5 bottom-5 bg-green-500 text-white p-3 rounded-full shadow-2xl hover:bg-green-600 transition-all duration-300 flex items-center justify-center z-[9999]"
      >
        💬
      </a>


      <section id="avantages" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center">
            {/* Partie Image */}
            <div
              className="w-full lg:w-1/2 mb-12 lg:mb-0"
              data-aos="fade-right"
            >
              <div className="relative max-w-2xl mx-auto lg:max-w-none">
                <img
                  src={whyUs}
                  alt="Équipe professionnelle"
                  className="rounded-xl shadow-2xl w-full h-auto object-cover"
                />
                <div
                  className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 lg:-bottom-8 lg:-right-8 bg-blue-600 text-white p-4 sm:p-5 lg:p-6 rounded-xl shadow-xl w-48 sm:w-56 lg:w-64"
                  data-aos="fade-up"
                  data-aos-delay="300"
                >
                  <h4 className="font-bold text-lg sm:text-xl lg:text-2xl mb-1 sm:mb-2">
                    12+ Ans
                  </h4>
                  <p className="text-blue-100 text-xs sm:text-sm lg:text-base leading-tight sm:leading-relaxed">
                    D'expérience dans le déménagement à Montréal
                  </p>
                </div>
              </div>
            </div>

            {/* Partie Texte */}
            <div className="lg:w-1/2 lg:pl-11" data-aos="fade-left">
              <span
                className="font-semibold"
                style={{
                  fontFamily: "Arial, sans-serif",
                  fontSize: "1rem",
                  color: "#1976D2",
                }}
              >
                POURQUOI NOUS CHOISIR
              </span>
              <h2
                className="mt-4 mb-8"
                style={{
                  fontFamily: "Arial, sans-serif",
                  fontSize: "2.5rem",
                  fontWeight: "bold",
                  color: "#1e293b",
                }}
              >
                Excellence à Chaque Étape
              </h2>

              <div className="space-y-8">
                {/* Point 1 - Sécurité */}
                <div className="flex">
                  <div className="flex-shrink-0 mr-6">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3
                      className="mb-2"
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "1.25rem",
                        fontWeight: "bold",
                        color: "#1e293b",
                      }}
                    >
                      Sécurité Garantie
                    </h3>
                    <p
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "1rem",
                        color: "#6b7280",
                      }}
                    >
                      Assurance tous risques et matériel de protection premium
                      pour vos biens les plus précieux.
                    </p>
                  </div>
                </div>

                {/* Point 2 - Ponctualité */}
                <div className="flex">
                  <div className="flex-shrink-0 mr-6">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3
                      className="mb-2"
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "1.25rem",
                        fontWeight: "bold",
                        color: "#1e293b",
                      }}
                    >
                      Ponctualité
                    </h3>
                    <p
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "1rem",
                        color: "#6b7280",
                      }}
                    >
                      Respect strict des délais avec suivi GPS en temps réel de
                      votre déménagement.
                    </p>
                  </div>
                </div>

                {/* Point 3 - Prix */}
                <div className="flex">
                  <div className="flex-shrink-0 mr-6">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3
                      className="mb-2"
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "1.25rem",
                        fontWeight: "bold",
                        color: "#1e293b",
                      }}
                    >
                      Prix Transparents
                    </h3>
                    <p
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "1rem",
                        color: "#6b7280",
                      }}
                    >
                      Devis détaillé sans surprise. Pas de frais cachés, prix
                      fixes honorés.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Témoignages */}
      <section
        id="avis"
        className="pt-8 pb-24 bg-gradient-to-b from-gray-50 to-white"
      >
        <div className="max-w-7xl mx-auto px-6">
          {/* Titre */}
          <div className="text-center mb-12" data-aos="fade-up">
            <h1
              className="mb-6"
              style={{
                fontFamily: "Arial, sans-serif",
                fontSize: "3rem",
                fontWeight: "bold",
                color: "#1e293b",
              }}
            >
              Ce Que Disent Nos Clients
            </h1>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6 rounded-full"></div>
            <p
              className="max-w-2xl mx-auto"
              style={{
                fontFamily: "Arial, sans-serif",
                fontSize: "1.25rem",
                color: "#6b7280",
              }}
            >
              Plus de{" "}
              <span className="font-semibold text-blue-600">500 familles</span>{" "}
              nous ont fait confiance pour leur déménagement
            </p>
          </div>

          {/* Témoignages */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              {
                source: "Google",
                iconColor: "text-red-500",
                avis: "Service exceptionnel! L'équipe était ponctuelle, professionnelle et a pris un soin particulier de nos meubles. Déménagement sans stress, je recommande vivement!",
                auteur: "Marie-Claude L.",
                details: "Déménagement Montréal → Laval",
                delay: "100",
              },
              {
                source: "Facebook",
                iconColor: "text-blue-600",
                avis: "Prix très compétitif et service impeccable. Ils ont déménagé notre bureau en une journée sans aucun dommage. Très satisfait du professionnalisme de l'équipe.",
                auteur: "Jean-Pierre M.",
                details: "Déménagement commercial",
                delay: "200",
              },
              {
                source: "Google",
                iconColor: "text-red-500",
                avis: "Rapidité et efficacité au rendez-vous! Déménagement terminé en moins de temps que prévu. L'équipe était très organisée et respectueuse.",
                auteur: "Sophie T.",
                details: "Déménagement 3½ → Condo",
                delay: "300",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl shadow-xl p-8 relative transform transition duration-500 hover:scale-105 hover:shadow-2xl border border-gray-100"
                data-aos="fade-up"
                data-aos-delay={item.delay}
              >
                {/* Top badge + Stars */}
                <div className="flex items-center justify-between mb-6">
                  <span
                    className="inline-flex items-center gap-2 px-3 py-1 text-sm font-semibold rounded"
                    style={{ background: "#eef2ff", color: "#1e40af" }}
                  >
                    {item.source === "Google" ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 48 48"
                        className="w-5 h-5"
                      >
                        <path
                          fill="#FFC107"
                          d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.156,7.961,3.039l5.657-5.657C33.64,6.053,29.084,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
                        />
                        <path
                          fill="#FF3D00"
                          d="M6.306,14.691l6.571,4.819C14.655,16.107,18.961,14,24,14c3.059,0,5.842,1.156,7.961,3.039l5.657-5.657C33.64,6.053,29.084,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                        />
                        <path
                          fill="#4CAF50"
                          d="M24,44c5.161,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.76,36,24,36c-5.202,0-9.619-3.317-11.277-7.949l-6.5,5.02C9.533,39.556,16.227,44,24,44z"
                        />
                        <path
                          fill="#1976D2"
                          d="M43.611,20.083H42V20H24v8h11.303c-0.793,2.237-2.231,4.166-3.994,5.57c0,0,0.003-0.003,0.005-0.004l6.19,5.238C36.992,39.188,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
                        />
                      </svg>
                    ) : item.source === "Facebook" ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className="w-5 h-5"
                        fill="#1877F2"
                      >
                        <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.356c0-3.007 1.792-4.669 4.533-4.669 1.313 0 2.686.235 2.686.235v2.953h-1.513c-1.491 0-1.956.93-1.956 1.887v2.262h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
                      </svg>
                    ) : (
                      <svg
                        className={`w-5 h-5 ${item.iconColor}`}
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                      </svg>
                    )}
                    {item.source}
                  </span>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, idx) => (
                      <svg
                        key={idx}
                        className="w-5 h-5 fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>

                {/* Guillemets décoratifs */}
                <div className="absolute -top-5 -left-4 text-blue-600 text-6xl opacity-10">
                  "
                </div>

                {/* Quote */}
                <p
                  className="text-gray-700 mb-6 italic leading-relaxed"
                  style={{
                    fontFamily: "Arial, sans-serif",
                    fontSize: "1.05rem",
                  }}
                >
                  "{item.avis}"
                </p>

                {/* Footer */}
                <div className="flex items-center justify-between mt-4">
                  <div>
                    <p
                      className="font-semibold text-gray-800"
                      style={{ fontFamily: "Arial, sans-serif" }}
                    >
                      {item.auteur}
                    </p>
                    <p
                      className="text-gray-500"
                      style={{ fontFamily: "Arial, sans-serif" }}
                    >
                      {item.details}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold">
                    {item.auteur
                      .split(" ")
                      .map((s) => s[0])
                      .join("")
                      .slice(0, 2)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Statistiques */}
          <div
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-10"
            data-aos="fade-up"
            data-aos-delay="500"
          >
            {[
              { value: "4.9/5", label: "Note moyenne Google" },
              { value: "500+", label: "Clients satisfaits" },
              { value: "98%", label: "Taux de recommandation" },
              { value: "12+", label: "Années d'expérience" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-3">
                  {stat.value}
                </div>
                <p className="text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Services avec Skeleton */}
      <section
        id="services"
        className="py-20 bg-gray-50 w-full overflow-hidden"
      >
        <div className="text-center mb-16 px-7 sm:px-6" data-aos="fade-up">
          <h2 className="mb-4 text-4xl sm:text-5xl font-bold text-slate-800">
            Nos Services
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="max-w-2xl mx-auto text-lg text-gray-600">
            Des solutions complètes adaptées à tous vos besoins de transport
          </p>
        </div>

        <div className="flex flex-col md:flex-row justify-center gap-10 max-w-6xl mx-auto flex-wrap px-4 sm:px-6">
          {loading.services
            ? Array.from({ length: 3 }).map((_, index) => (
                <ServiceSkeleton key={index} />
              ))
            : services.map((service) => {
                const optimizedUrl = service.imageUrl?.includes(
                  "res.cloudinary.com"
                )
                  ? service.imageUrl.replace(
                      "/upload/",
                      "/upload/f_auto,q_auto,w_427,h_320,c_fill/"
                    )
                  : service.imageUrl || "/default-service.jpg";

                return (
                  <div
                    key={service.id}
                    className="service-card bg-white rounded-2xl shadow-lg overflow-hidden flex-1 min-w-[300px]"
                  >
                    <div className="relative h-80 overflow-hidden">
                      <img
                        src={optimizedUrl}
                        alt={service.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                    </div>
                    <div className="p-6 text-center">
                      <h3 className="mb-4 text-2xl font-bold text-slate-800">
                        {service.name}
                      </h3>
                      <p className="mb-6 text-gray-500">
                        {service.description}
                      </p>
                      <Link
                        to={`/${service.name
                          .replace(/\s+/g, "")
                          .toLowerCase()}`}
                        className="bg-blue-800 text-white px-6 py-2 font-semibold hover:bg-blue-700 transition-colors shadow-md inline-block rounded-md"
                      >
                        Demandez une soumission
                      </Link>
                    </div>
                  </div>
                );
              })}
        </div>
      </section>
      <section
        id="contact"
        className="relative py-24 bg-blue-900 text-white overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-800 to-blue-600"></div>
        </div>

        <div
          className="max-w-5xl mx-auto px-6 relative z-10 text-center"
          data-aos="fade-up"
        >
          <h2
            className="mb-8"
            style={{
              fontFamily: "Arial, sans-serif",
              fontSize: "2.5rem",
              fontWeight: "bold",
              color: "#ffffff",
            }}
          >
            Prêt pour un Déménagement Sans Stress?
          </h2>
          <p
            className="mb-10 max-w-3xl mx-auto leading-relaxed"
            style={{
              fontFamily: "Arial, sans-serif",
              fontSize: "1.25rem",
              color: "#dbeafe",
            }}
          >
            Obtenez une soumission gratuite en moins de 2 minutes et planifiez
            votre déménagement en toute confiance.
          </p>

          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <a
              href={`tel:${
                company?.phone?.replace(/[^0-9]/g, "") || "5149876543"
              }`}
              className="bg-white text-blue-800 font-bold py-4 px-8 shadow-xl hover:bg-blue-50 transition-all transform hover:scale-105 flex items-center justify-center"
              style={{ borderRadius: 6 }}
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                ></path>
              </svg>
              <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                {company?.phone || "(514) 987-6543"}
              </span>
            </a>

            <button
              className="border-2 border-white text-white font-bold py-4 px-8 hover:bg-white hover:text-blue-800 transition-all flex items-center justify-center"
              style={{ borderRadius: 6 }}
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                ></path>
              </svg>
              {/* Demande soumission */}
              <Link
                href="#services"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick("#services");
                }}
              >
                Demande soumission
              </Link>
            </button>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1200 120"
            xmlns="http://www.w3.org/2000/svg"
            style={{ fill: "#ffffff", transform: "scaleY(-1)" }}
          >
            <path
              d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
              opacity=".25"
            ></path>
            <path
              d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
              opacity=".5"
            ></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"></path>
          </svg>
        </div>
      </section>
    </>
  );
};

export default Home;
